/**
 * Dynamic Fields Sidebar Component
 * Coláiste Lurgan Management System - Sidebar for inserting dynamic email fields
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  User,
  Users,
  Home,
  GraduationCap,
  MapPin,
  Search,
  ChevronDown,
  ChevronRight,
  Euro,
  Zap,
  CheckCircle2,
  Sparkles
} from 'lucide-react';
import { ConditionBuilder } from './ConditionBuilder';

// Dynamic field categories with available fields
const getFieldCategories = (t) => [
  {
    id: 'student',
    name: t('fieldCategory.student'),
    icon: User,
    color: 'bg-blue-100 text-blue-700',
    fields: [
      { code: '{{student.first_name}}', label: t('field.student.firstName') },
      { code: '{{student.last_name}}', label: t('field.student.lastName') },
      { code: '{{student.full_name}}', label: t('field.student.fullName') },
      { code: '{{student.student_id}}', label: t('field.student.studentId') },
      { code: '{{student.birthday}}', label: t('field.student.dateOfBirth') },
      { code: '{{student.age}}', label: t('field.student.age') },
      { code: '{{student.gender}}', label: t('field.student.genderCode') },
      { code: '{{student.gender_english}}', label: t('field.student.genderEnglish') },
      { code: '{{student.gender_irish}}', label: t('field.student.genderIrish') },
      { code: '{{student.school}}', label: t('field.student.currentSchool') },
      { code: '{{student.school_year.year}}', label: t('field.student.schoolYear') },
      { code: '{{student.home_county}}', label: t('field.student.homeCounty') }
    ]
  },
  {
    id: 'parent',
    name: t('fieldCategory.parent'),
    icon: Users,
    color: 'bg-green-100 text-green-700',
    fields: [
      { code: '{{student.parent_name}}', label: t('field.parent.name') },
      { code: '{{student.parent_email}}', label: t('field.parent.email') },
      { code: '{{student.parent_phone}}', label: t('field.parent.phone') },
      // { code: '{{student.emergency_contact}}', label: t('field.parent.emergencyContact') },
      // { code: '{{student.emergency_phone}}', label: t('field.parent.emergencyPhone') }
    ]
  },
  {
    id: 'address',
    name: t('fieldCategory.address'),
    icon: Home,
    color: 'bg-purple-100 text-purple-700',
    fields: [
      { code: '{{student.address_1}}', label: t('field.address.line1') },
      { code: '{{student.address_2}}', label: t('field.address.line2') },
      { code: '{{student.address_3}}', label: t('field.address.line3') },
      { code: '{{student.address_4}}', label: t('field.address.line4') },
      { code: '{{student.eircode}}', label: t('field.address.eircode') }
    ]
  },
  {
    id: 'course',
    name: t('fieldCategory.course'),
    icon: GraduationCap,
    color: 'bg-orange-100 text-orange-700',
    fields: [
      { code: '{{student.course.name}}', label: t('field.course.name') },
      { code: '{{student.course.start_date}}', label: t('field.course.startDate') },
      { code: '{{student.course.end_date}}', label: t('field.course.endDate') },
      { code: '{{student.course.description}}', label: t('field.course.description') },
      { code: '{{student.house.name}}', label: t('field.house.name') },
      { code: '{{student.house.address}}', label: t('field.house.address') },
      { code: '{{student.house.house_owner.get_full_name}}', label: t('field.house.owner') },
      { code: '{{student.class_group.name}}', label: t('field.class.name') },
      { code: '{{student.class_group.teacher.get_full_name}}', label: t('field.class.teacher') }
    ]
  },
  {
    id: 'financial',
    name: t('fieldCategory.financial'),
    icon: Euro,
    color: 'bg-emerald-100 text-emerald-700',
    fields: [
      { code: '{{course_price}}', label: t('field.financial.coursePrice') },
      { code: '{{total_paid}}', label: t('field.financial.totalPaid') },
      { code: '{{balance_due}}', label: t('field.financial.balanceDue') },
      { code: '{{deposit_paid}}', label: t('field.financial.depositPaid') }
    ]
  },
  {
    id: 'travel',
    name: t('fieldCategory.travel'),
    icon: MapPin,
    color: 'bg-indigo-100 text-indigo-700',
    fields: [
      { code: '{{student.travel_location.name}}', label: t('field.travel.location') },
      { code: '{{student.travel_location.address}}', label: t('field.travel.address') },
      { code: '{{student.travel_required}}', label: t('field.travel.required') },
      { code: '{{student.travel_notes}}', label: t('field.travel.notes') }
    ]
  }
];


export function DynamicFieldsSidebar({ onInsertField, visible = true, initialExpanded = ['student', 'parent'] }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(initialExpanded);
  const [recentlyInserted, setRecentlyInserted] = useState(null);
  const [conditionBuilderOpen, setConditionBuilderOpen] = useState(false);

  if (!visible) return null;

  const fieldCategories = getFieldCategories(t);

  // Filter fields based on search term and sort alphabetically
  const filteredCategories = fieldCategories.map(category => ({
    ...category,
    fields: category.fields
      .filter(field =>
        field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        field.code.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => a.label.localeCompare(b.label))
  })).filter(category => category.fields.length > 0);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Handle field insertion with visual feedback
  const handleFieldClick = (fieldCode, fieldLabel) => {
    onInsertField(fieldCode);

    // Show visual feedback
    setRecentlyInserted(fieldLabel);
    setTimeout(() => setRecentlyInserted(null), 2000);
  };


  return (
    <div className="space-y-4">
        {/* Visual Feedback Toast */}
        {recentlyInserted && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
            <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
            <div className="text-sm text-green-800">
              <span className="font-medium">{recentlyInserted}</span> {t('dynamicFields.inserted', 'inserted')}
            </div>
          </div>
        )}

        {/* Usage Instructions */}
        <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
          <h4 className="text-xs font-semibold text-blue-900 mb-1.5 flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5" />
            {t('dynamicFields.howToUse')}
          </h4>
          <ul className="text-xs text-blue-800 space-y-0.5">
            <li>• {t('dynamicFields.clickToInsert')}</li>
            <li>• {t('dynamicFields.realDataReplaced')}</li>
          </ul>
        </div>

        {/* Conditional Content Builder */}
        <div className="space-y-3">
          <Button
            onClick={() => setConditionBuilderOpen(true)}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {t('dynamicFields.buildConditional', 'Build Conditional Content')}
          </Button>
          <p className="text-xs text-gray-600 text-center">
            {t('dynamicFields.conditionalDescription', 'Create smart messages with if/then logic')}
          </p>
        </div>

        <ConditionBuilder
          open={conditionBuilderOpen}
          onOpenChange={setConditionBuilderOpen}
          onInsert={(template) => handleFieldClick(template, 'Conditional Content')}
        />

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder={t('dynamicFields.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>


        {/* Field Categories */}
        <div className="space-y-2">
          {filteredCategories.map((category) => {
            const CategoryIcon = category.icon;
            const isExpanded = expandedCategories.includes(category.id);

            return (
              <div key={category.id} className="border rounded-lg">
                <Button
                  variant="ghost"
                  onClick={() => toggleCategory(category.id)}
                  className="w-full justify-between p-3 h-auto hover:bg-blue-50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-2">
                    <div className={`p-1 rounded ${category.color}`}>
                      <CategoryIcon className="h-4 w-4" />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-medium">{category.name}</span>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>

                {isExpanded && (
                  <div className="border-t bg-gray-50 p-3">
                    <div className="grid grid-cols-2 gap-2">
                      {category.fields.map((field) => (
                        <Button
                          key={field.code}
                          variant="outline"
                          size="sm"
                          onClick={() => handleFieldClick(field.code, field.label)}
                          className="h-auto py-2 px-3 text-left justify-start hover:bg-blue-50 hover:border-blue-300 transition-all group text-xs font-normal"
                          title={field.code}
                        >
                          <span className="truncate group-hover:text-blue-700">{field.label}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filteredCategories.length === 0 && searchTerm && (
          <div className="text-center py-4 text-gray-500 text-sm">
            <Search className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>{t('dynamicFields.noFields')}</p>
          </div>
        )}
    </div>
  );
}

export default DynamicFieldsSidebar;